import { useState, useEffect } from 'react';

import type { Provider, CollabEditProvider } from '@atlaskit/collab-provider';

import { fg } from '@confluence/feature-gating';

import {
	fetchContentAppearanceProperty,
	updateContentAppearancePropertyAfterOfflineMode,
	updateAppearanceForCollab,
} from './contentAppearance';
import type {
	ContentAppearance,
	UpdateContentAppearancePropertyBody,
} from './contentAppearanceTypes';

type ContentAppearanceButtonProps = {
	children: React.ReactNode;
	userOffline?: boolean;
	params: UpdateContentAppearancePropertyBody;
	apolloClient: any;
	updateContentAppearanceProperty: (params: UpdateContentAppearancePropertyBody) => void;
	collabEditProvider?: CollabEditProvider | (CollabEditProvider & Provider) | null;
	currentContentAppearance?: ContentAppearance;
	setContentAppearance: (appearance: ContentAppearance) => void;
};

export const ContentAppearanceButton = ({
	children,
	userOffline,
	params,
	apolloClient,
	updateContentAppearanceProperty,
	collabEditProvider,
	currentContentAppearance,
	setContentAppearance,
}: ContentAppearanceButtonProps) => {
	const [shouldFetchAppearance, setShouldFetchAppearance] = useState<boolean>(false);

	const fetchAndUpdateAfterOffline = async () => {
		const appearanceProperty = await fetchContentAppearanceProperty(
			params.contentId,
			params.propertyKey,
			apolloClient,
			'network-only',
		);

		const currentStateVersion = currentContentAppearance?.version || 0;

		if (fg('confluence_frontend_content_appearance_409_fixes')) {
			if (appearanceProperty?.version > currentStateVersion) {
				const newParams = {
					...params,
					propertyValue: appearanceProperty.appearance,
					versionNumber: appearanceProperty.version,
				};

				// Just need to update the state if it's out of date, no need to perform a mutation to update the current view
				setContentAppearance(appearanceProperty);
				updateAppearanceForCollab(collabEditProvider, newParams);
			}
		} else {
			await updateContentAppearancePropertyAfterOfflineMode(
				appearanceProperty,
				params,
				updateContentAppearanceProperty,
				collabEditProvider,
			);
		}
	};

	useEffect(() => {
		// should refetch contentAppearanceProperty after user reconnected
		setShouldFetchAppearance(!!userOffline);
	}, [userOffline]);

	if (!userOffline && shouldFetchAppearance) {
		void fetchAndUpdateAfterOffline();
	}

	return children;
};
